import { useCallback, useEffect, useMemo, useState } from 'react';

import { AgentLogDetailsExecutionLogsSection } from './AgentLogDetailsExecutionLogsSection';
import { AgentLogDetailsOutputSection } from './AgentLogDetailsOutputSection';
import { ExecutionLogsExpandCollapseButtons } from './ExecutionLogsExpandCollapseButtons';

import type {
  AgentExecutionLogEntry,
  AgentExecutionOutput
} from '~/api/agentLogs/types';
import {
  AGENT_LOG_EVENT,
  bodyIncludesAgentLogEvent
} from '~/entities/agentLogMarkers';

type AgentLogDetailsAccordionSectionsProps = {
  logs: AgentExecutionLogEntry[];
  output: AgentExecutionOutput;
};

export function AgentLogDetailsAccordionSections({
  logs,
  output
}: AgentLogDetailsAccordionSectionsProps) {
  const executionLogs = useMemo(
    () =>
      logs.filter(
        log =>
          !bodyIncludesAgentLogEvent(log.body, AGENT_LOG_EVENT.OUTPUT_MESSAGE)
      ),
    [logs]
  );
  const logIds = useMemo(
    () => executionLogs.map(log => log.id),
    [executionLogs]
  );
  const [isExecutionLogsExpanded, setIsExecutionLogsExpanded] = useState(true);
  const [isOutputExpanded, setIsOutputExpanded] = useState(true);
  const [expandedLogIds, setExpandedLogIds] = useState<Set<string>>(
    () => new Set(logIds)
  );
  const [nestedAccordionsExpanded, setNestedAccordionsExpanded] =
    useState(true);
  const [accordionEpoch, setAccordionEpoch] = useState(0);

  useEffect(() => {
    setExpandedLogIds(new Set(logIds));
    setNestedAccordionsExpanded(true);
    setIsExecutionLogsExpanded(true);
    setIsOutputExpanded(true);
    setAccordionEpoch(previous => previous + 1);
  }, [logIds]);

  const handleExpandAll = useCallback(() => {
    setIsExecutionLogsExpanded(true);
    setIsOutputExpanded(true);
    setExpandedLogIds(new Set(logIds));
    setNestedAccordionsExpanded(true);
    setAccordionEpoch(previous => previous + 1);
  }, [logIds]);

  const handleCollapseAll = useCallback(() => {
    setIsExecutionLogsExpanded(false);
    setIsOutputExpanded(false);
    setExpandedLogIds(new Set());
    setNestedAccordionsExpanded(false);
    setAccordionEpoch(previous => previous + 1);
  }, []);

  const handleToggleLogExpanded = useCallback(
    (logId: string, expanded: boolean) => {
      setExpandedLogIds(previous => {
        const next = new Set(previous);
        if (expanded) {
          next.add(logId);
        } else {
          next.delete(logId);
        }
        return next;
      });
    },
    []
  );

  return (
    <>
      <ExecutionLogsExpandCollapseButtons
        onExpandAll={handleExpandAll}
        onCollapseAll={handleCollapseAll}
      />
      <AgentLogDetailsExecutionLogsSection
        logs={executionLogs}
        isSectionExpanded={isExecutionLogsExpanded}
        onSectionExpandedChange={setIsExecutionLogsExpanded}
        expandedLogIds={expandedLogIds}
        onToggleLogExpanded={handleToggleLogExpanded}
        nestedAccordionsExpanded={nestedAccordionsExpanded}
        accordionEpoch={accordionEpoch}
      />
      <AgentLogDetailsOutputSection
        output={output}
        isSectionExpanded={isOutputExpanded}
        onSectionExpandedChange={setIsOutputExpanded}
      />
    </>
  );
}
